import { call } from "@saas/api-client";
import { Alert, Badge, Button, Card, ErrorAlert, formatDate, Loading, PageHeader, SelectField, Table, TextField, useAsync, useCommand, usePendingId, useSession } from "@saas/ui";
import { useState, type FormEvent } from "react";
import { api } from "./api";

const ROLES = ["User", "TenantAdmin"] as const;

export function MembersPage() {
  const session = useSession();
  const members = useAsync(() => call(api.GET("/api/members")), []);
  const command = useCommand();
  const memberId = usePendingId();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("User");
  const [invited, setInvited] = useState<string>();

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    setInvited(undefined);
    const id = memberId.idFor(email, role);
    if (await command.run(() => call(api.POST("/api/members", { body: { id, email, role } })))) {
      memberId.done();
      setInvited(email);
      setEmail("");
      members.reload();
    }
  };

  const changeRole = async (id: string, newRole: string) => {
    if (await command.run(() => call(api.PUT("/api/members/{id}/role", { params: { path: { id } }, body: { role: newRole } })))) members.reload();
  };

  const remove = async (id: string) => {
    if (await command.run(() => call(api.DELETE("/api/members/{id}", { params: { path: { id } } })))) members.reload();
  };

  return (
    <>
      <PageHeader title="Members" description="People in your organisation and their roles. Invitations are emailed and bind the person to this tenant." />
      <Card title="Invite someone">
        <form className="sb-form" onSubmit={invite} noValidate>
          <div className="sb-form-row">
            <TextField label="Email" type="email" required value={email} error={command.fieldError("email")} onChange={(e) => setEmail(e.target.value)} />
            <SelectField label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </SelectField>
            <div className="sb-form-action">
              <Button type="submit" variant="primary" disabled={command.pending}>
                Send invitation
              </Button>
            </div>
          </div>
          {invited && <Alert tone="success">Invitation sent to {invited}.</Alert>}
        </form>
      </Card>
      <Card title="All members">
        <ErrorAlert error={command.error && !command.fieldError("email") ? command.error : members.error} />
        {members.loading && !members.data ? (
          <Loading />
        ) : (
          <Table caption={`${members.data?.length ?? 0} member(s)`} headers={["Email", "Role", "Status", "Invited", "Last sign-in", ""]}>
            {members.data?.map((m) => {
              const self = m.email === session.email;
              return (
                <tr key={m.id} data-testid={`member-${m.email}`}>
                  <td>
                    {m.email} {self && <span className="sb-muted">(you)</span>}
                  </td>
                  <td>
                    {self ? (
                      m.role
                    ) : (
                      <select aria-label={`Role of ${m.email}`} value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} className="sb-inline-select">
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <Badge tone={m.status === "Active" ? "success" : "neutral"}>{m.status}</Badge>
                  </td>
                  <td>{formatDate(m.invitedAt)}</td>
                  <td>{formatDate(m.lastSignInAt)}</td>
                  <td>
                    <div className="sb-actions">
                      {!self && (
                        <Button small variant="danger" onClick={() => remove(m.id)} aria-label={`Remove ${m.email}`}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>
    </>
  );
}
